import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';

const fallbackDescription = 'Tienda online con productos seleccionados, novedades, promociones y atención personalizada.';

// Limpia texto cargado desde el admin para usarlo en SEO sin HTML ni espacios raros.
const cleanText = (value = '') => String(value || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const currentOrigin = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const currentUrl = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}`;
};

const absoluteUrl = (value = '') => {
  const clean = String(value || '').trim();
  if (!clean) return '';
  if (/^https?:\/\//i.test(clean)) return clean;
  const origin = currentOrigin();
  if (!origin) return clean;
  return `${origin}${clean.startsWith('/') ? '' : '/'}${clean}`;
};

// Keywords base para busqueda organica. Combina datos de la tienda con terminos
// generales de ecommerce y evita duplicados.
const buildKeywords = (client, storeName) => {
  const base = [
    client?.key_words,
    storeName,
    'tienda online',
    'comprar online',
    'ofertas',
    'novedades',
    'envíos',
    'promociones',
    'catálogo online',
  ];

  return base
    .filter(Boolean)
    .join(', ')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .join(', ');
};

const StoreMarketingMeta = ({ client }) => {
  const storeName = cleanText(client?.name_show || client?.name || 'Tienda online');
  const rawDescription = cleanText(client?.description);
  const description = rawDescription || fallbackDescription;
  const title = `${storeName} | Tienda online`;
  const image = absoluteUrl(client?.image || client?.favicon || '');
  const favicon = absoluteUrl(client?.favicon || client?.image || '');
  const canonical = absoluteUrl(client?.url || '') || currentUrl();
  const keywords = buildKeywords(client, storeName);
  const socialLinks = [client?.link_ig, client?.link_fb].filter(Boolean);

  // JSON-LD ayuda a buscadores y redes a entender que la pagina es una tienda.
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: storeName,
    url: canonical,
    description,
    logo: image || favicon || undefined,
    image: image || undefined,
    sameAs: socialLinks.length ? socialLinks : undefined,
  };

  return (
    <Helmet>
      <html lang="es-AR" />
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="theme-color" content="#111827" />
      <link rel="canonical" href={canonical} />
      {!!favicon && <link rel="icon" href={favicon} />}
      {!!favicon && <link rel="apple-touch-icon" href={favicon} />}

      <meta itemProp="name" content={storeName} />
      <meta itemProp="description" content={description} />
      {!!image && <meta itemProp="image" content={image} />}

      <meta property="og:locale" content="es_AR" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={storeName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {!!image && <meta property="og:image" content={image} />}
      {!!image && <meta property="og:image:alt" content={storeName} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {!!image && <meta name="twitter:image" content={image} />}

      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

StoreMarketingMeta.defaultProps = {
  client: null,
};

StoreMarketingMeta.propTypes = {
  client: PropTypes.shape({
    description: PropTypes.string,
    favicon: PropTypes.string,
    image: PropTypes.string,
    key_words: PropTypes.string,
    link_fb: PropTypes.string,
    link_ig: PropTypes.string,
    name: PropTypes.string,
    name_show: PropTypes.string,
    url: PropTypes.string,
  }),
};

export default StoreMarketingMeta;
